import axios from "axios";
import { baseURL } from "../Constants";
import { track } from "../utils/track";

const url = `${baseURL}/Minigame`;

// ---- Runs (server-scored play behind the games leaderboard) ----
//
// Every leaderboard game is played as a run: the server holds the round's answers and scores each
// guess, so the score on the board is the server's count. The calls are the same for every game;
// the typed wrappers below each game just name the round/result shapes.

// The login token, when there is one: a run started with it is ranked on the leaderboard.
const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface RunView<TRound> {
    runId: string;
    score: number;
    /** False when the run was started logged out: it plays the same but never ranks. */
    ranked: boolean;
    round: TRound;
}

export interface RunGuessResult {
    correct: boolean;
    roundWon: boolean;
    runOver: boolean;
    timedOut: boolean;
    score: number;
}

const startRun = async <TRound,>(game: string, difficulty: string, extra: Record<string, string> = {}): Promise<RunView<TRound> | null> => {
    try {
        const response = await axios.post(`${url}/runs`, { game, difficulty, ...extra }, { headers: authHeaders() });
        track("game-round", { game, difficulty, ...extra });
        return response.data;
    } catch (error) {
        console.error(`Error starting ${game} run:`, error);
        return null;
    }
};

const nextRound = async <TRound,>(game: string, runId: string): Promise<RunView<TRound> | null> => {
    try {
        const response = await axios.post(`${url}/runs/${runId}/next`, null, { headers: authHeaders() });
        track("game-round", { game });
        return response.data;
    } catch (error) {
        console.error(`Error dealing ${game} round:`, error);
        return null;
    }
};

const guessRun = async <TResult extends RunGuessResult,>(game: string, runId: string, body: object): Promise<TResult | null> => {
    try {
        const response = await axios.post(`${url}/runs/${runId}/guess`, body, { headers: authHeaders() });
        track("game-guess", { game, correct: response.data?.correct });
        return response.data;
    } catch (error) {
        console.error(`Error submitting ${game} guess:`, error);
        return null;
    }
};

// Give up, or the clock ran out: ends the run and reveals the round.
const endRun = async <TReveal,>(game: string, runId: string): Promise<TReveal | null> => {
    try {
        const response = await axios.post(`${url}/runs/${runId}/end`, null, { headers: authHeaders() });
        return response.data.reveal;
    } catch (error) {
        console.error(`Error ending ${game} run:`, error);
        return null;
    }
};

// ---- Logo Puzzle ----

export interface LogoPuzzleRoundView {
    roundNo: number;
    teamName: string;
    division: string;
    season: number;
    logoUrl: string;
    grid: number;
    /** The dealt scramble: tiles[position] = piece. Solved when every piece sits at its own index. */
    tiles: number[];
}

export interface LogoPuzzleSolveResult extends RunGuessResult {
    moves: number;
    /** The server's time, dealt to solved. */
    elapsedMs: number;
}

// retryOf ("shuffle again") re-deals the same crest with a new scramble.
export const startLogoPuzzleRun = (difficulty: string, retryOf?: string) =>
    startRun<LogoPuzzleRoundView>("logo-puzzle", difficulty, retryOf ? { retryOf } : {});

// The swaps are replayed server-side on the dealt scramble; only a solved board is accepted.
export const submitLogoPuzzleSolve = (runId: string, swaps: [number, number][]) =>
    guessRun<LogoPuzzleSolveResult>("logo-puzzle", runId, { swaps });

// ---- Rating Duel (Higher or Lower + One in Five) ----

export type RatingDuelMode = "duel" | "five";

export interface RatingDuelPlayer {
    steamId: string;
    playerName: string;
    team: string;
    division: string;
}

export interface RatingDuelRoundView {
    roundNo: number;
    season: number;
    division: string;
    /** No ratings here — they stay on the server until the pick is in. */
    players: RatingDuelPlayer[];
    secondsLeft: number;
}

export interface RatingDuelReveal {
    /** Everyone sharing the top rating — any of them was a right pick. */
    topSteamIds: string[];
    ratings: Record<string, number>;
}

export interface RatingDuelGuessResult extends RunGuessResult {
    reveal: RatingDuelReveal | null;
}

export const startRatingDuelRun = (mode: RatingDuelMode, difficulty: string) =>
    startRun<RatingDuelRoundView>("rating-duel", difficulty, { mode });

export const nextRatingDuelRound = (runId: string) =>
    nextRound<RatingDuelRoundView>("rating-duel", runId);

export const submitRatingDuelGuess = (runId: string, steamId: string) =>
    guessRun<RatingDuelGuessResult>("rating-duel", runId, { steamId });

export const endRatingDuelRun = (runId: string) =>
    endRun<RatingDuelReveal>("rating-duel", runId);

// ---- Team Roster ----

export interface TeamRosterCandidate {
    steamId: string;
    name: string;
}

export interface TeamRosterRoundView {
    roundNo: number;
    teamName: string;
    division: string;
    season: number;
    teamLogo: string | null;
    /** ~20 names; the correct 5 aren't marked — they stay on the server. */
    candidates: TeamRosterCandidate[];
    slots: number;
    secondsLeft: number;
    mistakesLeft: number;
}

export interface TeamRosterGuessResult extends RunGuessResult {
    mistakesLeft: number;
    /** The correct 5 — only once the round is over. */
    reveal: TeamRosterCandidate[] | null;
}

export const startTeamRosterRun = (difficulty: string) =>
    startRun<TeamRosterRoundView>("team-roster", difficulty);

export const nextTeamRosterRound = (runId: string) =>
    nextRound<TeamRosterRoundView>("team-roster", runId);

export const submitTeamRosterGuess = (runId: string, steamId: string) =>
    guessRun<TeamRosterGuessResult>("team-roster", runId, { steamId });

export const endTeamRosterRun = (runId: string) =>
    endRun<TeamRosterCandidate[]>("team-roster", runId);

// ---- Who's That Pro? ----

export interface WhosThatProRoundView {
    roundNo: number;
    /** The four name options for each band, in band order. The answers stay on the server. */
    options: string[][];
    secondsLeft: number;
    strikes: number;
    /** Each band's width/height ratio. Uneven by design — the bands are cut on facial landmarks,
     *  so hair/forehead is much taller than the eye or nose strip. */
    bandAspects: number[];
}

export type WhosThatProRunView = RunView<WhosThatProRoundView>;

export interface WhosThatProReveal {
    name: string;
    steamId: string;
}

export interface WhosThatProGuessResult extends RunGuessResult {
    strikes: number;
    /** All four answers — only once the round is over. */
    reveal: WhosThatProReveal[] | null;
}

// One band of the face-normalised photo for the run's current round. The backend does the cropping
// (and the hard-mode blur) from the run's server-side state, so neither the player's id nor the
// full photo reaches the browser — serving the whole image let you read the answer straight out of
// the network tab.
export const whosThatProPhotoUrl = (runId: string, roundNo: number, band: number): string =>
    `${url}/runs/${runId}/photo?round=${roundNo}&band=${band}`;

export const startWhosThatProRun = (difficulty: string) =>
    startRun<WhosThatProRoundView>("whos-that-pro", difficulty);

export const nextWhosThatProRound = (runId: string) =>
    nextRound<WhosThatProRoundView>("whos-that-pro", runId);

export const submitWhosThatProGuess = (runId: string, band: number, name: string) =>
    guessRun<WhosThatProGuessResult>("whos-that-pro", runId, { band, name });

export const endWhosThatProRun = (runId: string) =>
    endRun<WhosThatProReveal[]>("whos-that-pro", runId);

// ---- Games leaderboard ----

export type BoardPeriod = "month" | "all";

export interface GameBoardEntry {
    rank: number;
    steamId: string;
    name: string;
    score: number;
    moves: number | null;
    achievedAt: string;
    premium: boolean;
    donator: boolean;
}

export interface GameBoard {
    game: string;
    variant: string;
    period: BoardPeriod;
    entries: GameBoardEntry[];
    /** The viewer's own best on this board, when logged in and ranked. */
    me: GameBoardEntry | null;
    totalPlayers: number;
}

export const getGameLeaderboard = async (
    game: string,
    variant: string,
    period: BoardPeriod,
    limit = 50
): Promise<GameBoard | null> => {
    try {
        const response = await axios.get(`${url}/leaderboard`, {
            params: { game, variant, period, limit },
            headers: authHeaders()
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching game leaderboard:", error);
        return null;
    }
};
